import * as React from 'react';

import { keyframes } from '@emotion/react';
import { CircularProgress, Collapse, Typography } from '@mui/material';
import { COMPOSE_EMAIL_TOOL } from '@realadvisor/common-types';
import { Brain, ChevronRight, ShieldQuestion } from 'lucide-react';

import {
  ApprovalActions,
  type ApprovalResponseHandler,
  type ToolPart,
  statusOf,
} from '@modules/_shared/components/chat/ApprovalActions';
import {
  ThemeSwitchLink,
  ToolCallDetails,
  ToolPartStatusIcon,
  toolIconOf,
  useSandboxFailureCaption,
  useToolLabel,
  useVisualFailureCaption,
  useWriteCaption,
} from '@modules/_shared/components/chat/ChatToolCall';
import { ComposeEmailSentView } from '@modules/_shared/components/chat/ComposeEmailToolCard';
import { useLocale } from '@shared/hooks/locale';
import { useTheme } from '@shared/theme/theme';

// "Thinking" block (beautifului.dev Thinking pattern): consecutive reasoning
// and tool parts of an assistant message render as one timeline instead of a
// stack of cards. Steps appear one by one while the agent works, then the block
// collapses to a one-line summary once the answer starts.

export type ChatStep =
  | { kind: 'reasoning'; text: string }
  | { kind: 'tool'; part: ToolPart };

// When the current turn was sent (ms epoch), provided by the chat panel. The
// pre-response "Thinking…" indicator and the first steps block of the reply
// both count from it, so the timer runs on instead of restarting at 0 when the
// first step arrives.
export const ChatTurnStartContext = React.createContext<number | null>(null);

const stepIn = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: translateY(0); }
`;

const shimmer = keyframes`
  from { background-position: 100% 0; }
  to { background-position: -100% 0; }
`;

const ICON_SIZE = 22;

const firstSentence = (text: string): string => {
  const trimmed = text.trim().replace(/\s+/g, ' ');
  const match = trimmed.match(/^.+?[.!?](\s|$)/);
  return (match?.[0] ?? trimmed).trim();
};

const useNow = (ticking: boolean): number => {
  const [now, setNow] = React.useState(() => Date.now());
  React.useEffect(() => {
    if (!ticking) {
      return;
    }
    setNow(Date.now());
    const interval = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(interval);
  }, [ticking]);
  return now;
};

const ThinkingLabel = ({ seconds }: { seconds: number | null }) => {
  const { colors } = useTheme();
  const { t } = useLocale();
  return (
    <Typography
      variant="body2"
      sx={{
        fontWeight: 500,
        background: `linear-gradient(90deg, ${colors.grey500} 0%, ${colors.grey800} 50%, ${colors.grey500} 100%)`,
        backgroundSize: '200% 100%',
        backgroundClip: 'text',
        WebkitBackgroundClip: 'text',
        color: 'transparent',
        animation: `${shimmer} 1.6s linear infinite`,
      }}
    >
      {t('Thinking…')}
      {seconds != null ? ` ${seconds}s` : ''}
    </Typography>
  );
};

// Shown while the agent works and nothing visible has streamed back yet: below
// the user's message before the reply exists (`inMessage` false), then inside
// the reply until its first step or text arrives. Both land exactly where the
// ChatSteps header takes over, and all three count from the turn start, so the
// label never moves or restarts.
export const ThinkingIndicator = ({
  inMessage = false,
}: {
  inMessage?: boolean;
}) => {
  const turnStartedAt = React.useContext(ChatTurnStartContext);
  const [mountedAt] = React.useState(() => Date.now());
  const now = useNow(true);
  const start = turnStartedAt ?? mountedAt;
  const seconds = Math.max(0, Math.round((now - start) / 1000));
  return (
    // Inside a message the message's own padding already applies; `2px 0`
    // matches the ChatSteps header button.
    <div role="status" css={{ padding: inMessage ? '2px 0' : '6px 2px' }}>
      <ThinkingLabel seconds={seconds} />
    </div>
  );
};

// Label slot while a tool call's input streams in without a title yet.
const LabelPlaceholder = () => {
  const { colors } = useTheme();
  return (
    <span
      aria-hidden
      css={{
        display: 'block',
        width: 140,
        maxWidth: '100%',
        height: 10,
        borderRadius: 5,
        background: `linear-gradient(90deg, ${colors.grey100} 0%, ${colors.divider} 50%, ${colors.grey100} 100%)`,
        backgroundSize: '200% 100%',
        animation: `${shimmer} 1.2s linear infinite`,
      }}
    />
  );
};

const StepIcon = ({ children }: { children: React.ReactNode }) => {
  const { colors } = useTheme();
  return (
    <span
      css={{
        width: ICON_SIZE,
        height: ICON_SIZE,
        borderRadius: '50%',
        backgroundColor: colors.grey100,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      {children}
    </span>
  );
};

// One timeline row: icon + connector line on the left, label + status on the
// right, click to expand the details below the label. Rows start collapsed;
// `footer` stays visible either way (the approval buttons).
const StepRow = ({
  icon,
  label,
  tag,
  preview,
  previewWrap = false,
  status,
  isLast,
  footer,
  children,
}: {
  icon: React.ReactNode;
  // null renders a placeholder (input still streaming, no title yet).
  label: string | null;
  tag?: React.ReactNode;
  preview?: string;
  // The approval caption carries the warning ("All records", "and more") at
  // the end, so it must wrap instead of being clipped in the narrow panel.
  previewWrap?: boolean;
  status: React.ReactNode;
  isLast: boolean;
  footer?: React.ReactNode;
  children: React.ReactNode;
}) => {
  const { colors } = useTheme();
  const [open, setOpen] = React.useState(false);

  return (
    <div
      css={{
        display: 'flex',
        gap: 8,
        animation: `${stepIn} 0.25s ease-out`,
      }}
    >
      <div
        css={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          flexShrink: 0,
        }}
      >
        <StepIcon>{icon}</StepIcon>
        {!isLast && (
          <span
            css={{
              flex: 1,
              width: 1,
              minHeight: 8,
              backgroundColor: colors.divider,
            }}
          />
        )}
      </div>
      <div css={{ flex: 1, minWidth: 0, paddingBottom: isLast ? 0 : 8 }}>
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          aria-expanded={open}
          css={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            width: '100%',
            minHeight: ICON_SIZE,
            padding: 0,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            textAlign: 'left',
            font: 'inherit',
            color: colors.grey700,
            '&:hover .chat-step-chevron': { opacity: 1 },
          }}
        >
          <span css={{ flex: 1, minWidth: 0 }}>
            {label != null ? (
              <Typography
                variant="body2"
                sx={{ fontWeight: 500 }}
                noWrap
                title={label}
              >
                {label}
              </Typography>
            ) : (
              <LabelPlaceholder />
            )}
          </span>
          {tag}
          <ChevronRight
            className="chat-step-chevron"
            size={13}
            color={colors.grey500}
            style={{
              opacity: open ? 1 : 0,
              transform: open ? 'rotate(90deg)' : 'none',
              transition: 'transform 0.15s ease, opacity 0.15s ease',
              flexShrink: 0,
            }}
          />
          {status}
        </button>
        {preview != null && preview.length > 0 && !open && (
          <Typography
            variant="caption"
            sx={{ display: 'block', color: colors.grey500 }}
            noWrap={!previewWrap}
            title={preview}
          >
            {preview}
          </Typography>
        )}
        <Collapse in={open} unmountOnExit>
          <div css={{ marginTop: 4 }}>{children}</div>
        </Collapse>
        {footer}
      </div>
    </div>
  );
};

type ApprovalState = 'pending' | 'approved' | 'denied';

const approvalStateOf = (part: ToolPart): ApprovalState | null => {
  const status = statusOf(part);
  if (status === 'approval-requested') {
    return 'pending';
  }
  if (status === 'denied' || part.approval?.approved === false) {
    return 'denied';
  }
  if (part.approval?.approved === true) {
    return 'approved';
  }
  return null;
};

// The label stays the agent's title in every approval state; this tag says
// where the write stands, so the row never has to be relabelled.
const ApprovalTag = ({ state }: { state: ApprovalState }) => {
  const { colors } = useTheme();
  const { t } = useLocale();
  const { label, color } =
    state === 'pending'
      ? { label: t('Needs approval'), color: colors.warningText }
      : state === 'denied'
      ? { label: t('Denied'), color: colors.errorText }
      : { label: t('Approved'), color: colors.grey600 };
  return (
    <span
      css={{
        flexShrink: 0,
        padding: '0 6px',
        borderRadius: 10,
        border: `1px solid ${color}`,
        color,
        fontSize: '0.7rem',
        lineHeight: '16px',
        fontWeight: 500,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </span>
  );
};

const ToolStep = ({
  part,
  isLast,
  onApprovalResponse,
}: {
  part: ToolPart;
  isLast: boolean;
  onApprovalResponse?: ApprovalResponseHandler;
}) => {
  const { colors } = useTheme();
  const label = useToolLabel(part);
  const writeCaption = useWriteCaption(part);
  const visualFailureCaption = useVisualFailureCaption(part);
  const sandboxFailureCaption = useSandboxFailureCaption(part);
  const Icon = toolIconOf(part.toolName);
  const status = statusOf(part);
  const approvalState = approvalStateOf(part);
  return (
    <StepRow
      icon={<Icon size={12} color={colors.grey600} />}
      label={label}
      preview={
        writeCaption ??
        visualFailureCaption ??
        sandboxFailureCaption ??
        undefined
      }
      previewWrap={writeCaption != null || sandboxFailureCaption != null}
      tag={approvalState != null ? <ApprovalTag state={approvalState} /> : null}
      status={<ToolPartStatusIcon part={part} />}
      isLast={isLast}
      footer={
        status === 'approval-requested' &&
        part.approval?.id != null &&
        onApprovalResponse != null ? (
          <ApprovalActions
            approvalId={part.approval.id}
            onApprovalResponse={onApprovalResponse}
          />
        ) : (
          <ThemeSwitchLink part={part} />
        )
      }
    >
      <div
        css={{
          border: `1px solid ${colors.divider}`,
          borderRadius: 8,
          backgroundColor: colors.white,
          overflow: 'hidden',
          paddingTop: 10,
        }}
      >
        {part.toolName === COMPOSE_EMAIL_TOOL ? (
          <div css={{ padding: '0 10px 10px 10px' }}>
            <ComposeEmailSentView part={part} />
          </div>
        ) : (
          <ToolCallDetails part={part} showApproval={false} />
        )}
      </div>
    </StepRow>
  );
};

const ReasoningStep = ({
  text,
  isLast,
  isStreaming,
}: {
  text: string;
  isLast: boolean;
  isStreaming: boolean;
}) => {
  const { colors } = useTheme();
  const { t } = useLocale();
  return (
    <StepRow
      icon={<Brain size={12} color={colors.grey600} />}
      label={t('Thinking')}
      preview={firstSentence(text)}
      status={
        isStreaming ? (
          <CircularProgress size={13} sx={{ color: colors.grey500 }} />
        ) : null
      }
      isLast={isLast}
    >
      <Typography
        variant="body2"
        sx={{
          color: colors.grey600,
          whiteSpace: 'pre-wrap',
          maxHeight: 220,
          overflow: 'auto',
        }}
      >
        {text}
      </Typography>
    </StepRow>
  );
};

// Seconds the agent actually spent working in this block: the sum of its
// active stretches, so time spent waiting on the user's approval (block
// inactive) is not counted. `firstStart` backdates the first stretch (turn
// send time). null for blocks that were never active here (loaded history).
const useActiveSeconds = (
  isActive: boolean,
  firstStart: number | null,
): number | null => {
  const accumulatedRef = React.useRef(0);
  const activeSinceRef = React.useRef<number | null>(
    isActive ? firstStart ?? Date.now() : null,
  );
  const everActiveRef = React.useRef(isActive);
  const now = useNow(isActive);
  const [, rerender] = React.useReducer((n: number) => n + 1, 0);

  React.useEffect(() => {
    if (isActive) {
      everActiveRef.current = true;
      activeSinceRef.current ??= Date.now();
    } else if (activeSinceRef.current != null) {
      accumulatedRef.current += Date.now() - activeSinceRef.current;
      activeSinceRef.current = null;
    }
    rerender();
  }, [isActive]);

  if (!everActiveRef.current) {
    return null;
  }
  const running =
    activeSinceRef.current != null
      ? Math.max(0, now - activeSinceRef.current)
      : 0;
  return Math.round((accumulatedRef.current + running) / 1000);
};

export const ChatSteps = ({
  steps,
  isActive,
  countFromTurnStart,
  onApprovalResponse,
}: {
  steps: Array<ChatStep>;
  // True while the agent is still working inside this block (streaming and no
  // answer text after it yet).
  isActive: boolean;
  // The block opens the reply: its timer continues the "Thinking…" indicator
  // shown since the turn was sent.
  countFromTurnStart: boolean;
  onApprovalResponse?: ApprovalResponseHandler;
}) => {
  const { colors } = useTheme();
  const { t } = useLocale();
  const turnStartedAt = React.useContext(ChatTurnStartContext);
  const hasPendingApproval = steps.some(
    step =>
      step.kind === 'tool' && statusOf(step.part) === 'approval-requested',
  );
  const seconds = useActiveSeconds(
    isActive,
    countFromTurnStart ? turnStartedAt : null,
  );

  // Open while the agent works, collapse once when it is done. A manual toggle
  // wins from then on; a pending approval always forces the block open.
  const [open, setOpen] = React.useState(isActive || hasPendingApproval);
  const userToggledRef = React.useRef(false);
  const wasActiveRef = React.useRef(isActive);
  React.useEffect(() => {
    if (hasPendingApproval) {
      setOpen(true);
    } else if (!userToggledRef.current && wasActiveRef.current !== isActive) {
      setOpen(isActive);
    }
    wasActiveRef.current = isActive;
  }, [isActive, hasPendingApproval]);

  const count = steps.length;
  const summary =
    seconds != null
      ? `${t('Thought for {{seconds}}s', { seconds })} · ${t(
          '{{count}} steps',
          { count },
        )}`
      : t('{{count}} steps', { count });

  return (
    <div>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => {
          // Approve/Deny live inside the block, so it stays open until the
          // user has answered.
          if (hasPendingApproval) {
            setOpen(true);
            return;
          }
          userToggledRef.current = true;
          setOpen(o => !o);
        }}
        css={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '2px 0',
          background: 'none',
          border: 'none',
          cursor: hasPendingApproval ? 'default' : 'pointer',
          textAlign: 'left',
          font: 'inherit',
          color: colors.grey600,
          maxWidth: '100%',
        }}
      >
        {hasPendingApproval ? (
          <>
            <ShieldQuestion
              size={14}
              color={colors.warningText}
              style={{ flexShrink: 0 }}
            />
            <Typography
              variant="body2"
              sx={{ fontWeight: 500, color: colors.warningText }}
              noWrap
            >
              {t('Waiting for your approval')}
            </Typography>
          </>
        ) : isActive ? (
          <ThinkingLabel seconds={seconds} />
        ) : (
          <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
            {summary}
          </Typography>
        )}
        {!hasPendingApproval && (
          <ChevronRight
            size={14}
            color={colors.grey500}
            style={{
              transform: open ? 'rotate(90deg)' : 'none',
              transition: 'transform 0.15s ease',
              flexShrink: 0,
            }}
          />
        )}
      </button>
      <Collapse in={open}>
        <div css={{ paddingTop: 8, paddingLeft: 2 }}>
          {steps.map((step, index) => {
            const isLast = index === steps.length - 1;
            return step.kind === 'reasoning' ? (
              <ReasoningStep
                key={index}
                text={step.text}
                isLast={isLast}
                isStreaming={isActive && isLast}
              />
            ) : (
              <ToolStep
                key={index}
                part={step.part}
                isLast={isLast}
                onApprovalResponse={onApprovalResponse}
              />
            );
          })}
        </div>
      </Collapse>
    </div>
  );
};
